import { CalendarDays, ClipboardList, FlaskConical, Plus } from "lucide-react";
import { useDemo } from "../context/DemoContext";
import { useAuth } from "../context/AuthContext";
import { DashboardReal } from "../real/RealScreens";
import { formatDateBR } from "@tracker-bet-analystic/domain";
import { FixtureCard } from "../components/FixtureCard";
import { EmptyState } from "../components/EmptyState";

export function Dashboard() {
  const { fixtures, analyses, isDemo } = useDemo();
  const { configured } = useAuth();

  if (configured && !isDemo) {
    return <DashboardReal />;
  }

  const upcomingFixtures = fixtures
    .filter((f) => f.status === "scheduled" && new Date(f.kickoffAt) > new Date())
    .slice(0, 5);

  const recentAnalyses = analyses.slice(0, 5);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text)]">Dashboard</h1>
        </div>
        <a href="/search" className="btn btn-primary">
          <Plus size={16} aria-hidden="true" /> Nova Análise
        </a>
      </div>

      <section aria-labelledby="upcoming-heading" className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 id="upcoming-heading" className="text-lg font-semibold">Próximas Partidas</h2>
          <a href="/search" className="text-sm text-[var(--color-primary)] hover:underline">Ver todas</a>
        </div>
        {upcomingFixtures.length > 0 ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {upcomingFixtures.map((fixture) => (
              <FixtureCard key={fixture.id} fixture={fixture} />
            ))}
          </div>
        ) : (
          <EmptyState
            icon={<CalendarDays />}
            title="Nenhuma partida agendada"
            text="Não há partidas futuras no momento. Tente novamente mais tarde."
          />
        )}
      </section>

      <section aria-labelledby="recent-heading">
        <div className="flex items-center justify-between mb-4">
          <h2 id="recent-heading" className="text-lg font-semibold">Análises Recentes</h2>
          <a href="/history" className="text-sm text-[var(--color-primary)] hover:underline">Ver histórico</a>
        </div>
        {recentAnalyses.length > 0 ? (
          <div className="card overflow-hidden">
            <div className="table-container">
              <table className="table" role="table">
                <thead>
                  <tr>
                    <th scope="col">Data</th>
                    <th scope="col">Partida</th>
                    <th scope="col">Competição</th>
                    <th scope="col">Status</th>
                    <th scope="col">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {recentAnalyses.map((analysis) => (
                    <tr key={analysis.fixture.id}>
                      <td>{formatDateBR(analysis.generatedAt)}</td>
                      <td className="font-medium">
                        {analysis.fixture.homeTeamName} vs {analysis.fixture.awayTeamName}
                      </td>
                      <td>{analysis.fixture.competitionName}</td>
                      <td>
                        <span
                          className={`badge ${
                            analysis.model ? "badge-success" : "badge-warning"
                          }`}
                        >
                          {analysis.model ? "Com modelo" : "Apenas descritivo"}
                        </span>
                      </td>
                      <td>
                        <a
                          href={`/report/${analysis.fixture.id}`}
                          className="btn btn-sm btn-ghost"
                        >
                          Ver
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <EmptyState
            icon={<ClipboardList />}
            title="Nenhuma análise realizada"
            text="Suas análises aparecerão aqui após criar a primeira."
            action={<a href="/search" className="btn btn-primary mt-4">Criar primeira análise</a>}
          />
        )}
      </section>

      {isDemo && (
        <div className="mt-8 p-4 bg-[var(--color-demo-light)] border border-[var(--color-demo)] rounded-lg">
          <h3 className="font-semibold text-[var(--color-demo)] mb-2"><FlaskConical size={16} aria-hidden="true" /> Modo Demonstração</h3>
          <p className="text-sm text-[var(--color-text-muted)]">
            Todos os dados exibidos são sintéticos. As análises usam o motor estatístico experimental
            (Poisson independente v0.1) com limitações conhecidas: independência entre gols, pequena amostra,
            ausência de ajuste por força e sobredispersão não modelada.
          </p>
        </div>
      )}
    </div>
  );
}
